// src/realtime/LayoutChart/ChartPanel.jsx
import React, { useState, useEffect, useRef } from 'react';
import { createChart } from 'lightweight-charts';
import {
  Box,
  Autocomplete,
  TextField,
  IconButton,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import LoopIcon from '@mui/icons-material/Loop';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { useTheme } from '@mui/material';
import { tokens } from '../../../theme';
import { useFetchingData } from '../../../components/api.js';

const BOT_TOKEN = '7982681405:AAHjvdpbERCNbu9jm5-ZkfV3_oTMWM67_68';
const CHAT_ID   = '-1002663905326';

export default function ChartPanel({
  symbol,
  onSymbolChange,
  symbolsList,
  baseApiUrl,
  fullscreen = false,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { __fetching_data__ } = useFetchingData();

  // ─── State & refs ───────────────────────────────────────────
  const [data, setData]       = useState(null);
  const [flowAcc, setFlowAcc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hToolActive, setHToolActive] = useState(false);
  const [hTarget, setHTarget]         = useState('flow');
  const hTargetRef = useRef(hTarget);
  useEffect(() => { hTargetRef.current = hTarget; }, [hTarget]);

  const containerRef    = useRef();
  const chartRef        = useRef();
  const priceSeriesRef  = useRef();
  const flowSeriesRef   = useRef();
  const hLineRef        = useRef(null);
  const hLinePriceRef   = useRef(null);
  const notifiedRef     = useRef(false);
  const zoomedRef       = useRef(false);
  const prevRange       = useRef(null);
  const hToolRef        = useRef(false);

  // ─── 1) Fetch & filter today's data every 10s ───────────────
  useEffect(() => {
    if (!symbol) return;
    let alive = true;

    async function fetchData() {
      setLoading(true);
      const { data: raw } = await __fetching_data__({
        url: `${baseApiUrl}/realtime_ws_data?symbol=${symbol.toUpperCase()}`,
      });
      if (!alive) return;

      const now      = new Date();
      const dayStart = Math.floor(new Date(now).setHours(0, 0, 0, 0) / 1000);
      const dayEnd   = Math.floor(new Date(now).setHours(23, 59, 59, 999) / 1000);

      const pts = raw
        .map(i => {
          const d = new Date(i.time);
          d.setHours(d.getHours() + 7);
          return { time: d.getTime() / 1000, value: i.last };
        })
        .filter(p => p.time >= dayStart && p.time <= dayEnd);

      const flw = raw
        .map(i => {
          const d = new Date(i.time);
          d.setHours(d.getHours() + 7);
          return { time: d.getTime() / 1000, value: i.flow };
        })
        .filter(p => p.time >= dayStart && p.time <= dayEnd);

      setData(pts.length ? pts : null);
      setFlowAcc(flw.length ? flw : null);
      setLoading(false);
    }

    fetchData();
    const iv = setInterval(fetchData, 10_000);
    return () => { alive = false; clearInterval(iv); };
  }, [symbol]);

  // ─── 2a) Initialize chart + series + click handler ONCE ──────
  useEffect(() => {
    if (!containerRef.current) return;

    const chart = createChart(containerRef.current, {
      width:  containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
      layout: {
        background: { color: theme.palette.table.odd },
        textColor:  theme.palette.text.secondary,
      },
      grid: {
        vertLines: { color: theme.palette.neutral.dark },
        horzLines: { color: theme.palette.neutral.dark },
      },
      timeScale: { timeVisible: true, secondsVisible: false },
      rightPriceScale: { visible: true, borderVisible: false },
      leftPriceScale:  { visible: true, borderVisible: false, position: 'left' },
      localization:    { locale: 'th-TH' },
    });
    chartRef.current = chart;

    // — price series on RIGHT
    priceSeriesRef.current = chart.addLineSeries({
      priceScaleId: 'right',
      color:        colors.whiteChart[200],
    });

    // — flow baseline on LEFT
    flowSeriesRef.current = chart.addBaselineSeries({
      priceScaleId:    'left',
      baseValue:       { type:'price', price: flowAcc?.[0]?.value||0 },
      topLineColor:    colors.greenChart[100],
      bottomLineColor: colors.redChart[100],
      topFillColor1:   'rgba(0,0,0,0)',
      bottomFillColor1:'rgba(0,0,0,0)',
      priceFormat: {
        type: 'custom',
        minMove: 1e-8,
        formatter: price => {
          const absPrice = Math.abs(price);
          if (absPrice >= 1e9)   return (price / 1e9).toFixed(3) + 'B';
          if (absPrice >= 1e6)   return (price / 1e6).toFixed(1) + 'M';
          if (absPrice >= 1e5)   return (price / 1e5).toFixed(1) + 'K';
          if (absPrice >= 1e4)   return (price / 1e4).toFixed(2) + 'K';
          if (absPrice >= 1e3)   return (price / 1e3).toFixed(3) + 'K';
          return price.toFixed(0);
        },
      },
    });

    // — click to place/move H-Line
    chart.subscribeClick(param => {
      if (!hToolRef.current || !param.point) return;
      const target = hTargetRef.current === 'price'
        ? priceSeriesRef.current
        : flowSeriesRef.current;
      const p = target.coordinateToPrice(param.point.y);

      if (hLineRef.current) {
        hLineRef.current.applyOptions({ price: p });
      } else {
        hLineRef.current = target.createPriceLine({
          price:            p,
          color:            'red',
          lineWidth:        2,
          lineStyle:        2,
          axisLabelVisible: true,
          title:            'H-Line',
        });
      }
      hLinePriceRef.current = p;
      notifiedRef.current   = false;
    });

    // — detect manual zoom vs auto-fit
    let minF, maxT;
    chart.timeScale().subscribeVisibleLogicalRangeChange(rng => {
      if (!rng) return;
      if (minF == null) [minF,maxT] = [rng.from,rng.to];
      if (prevRange.current) {
        const changed =
          Math.abs(prevRange.current.from - rng.from) > 1e-6 ||
          Math.abs(prevRange.current.to   - rng.to) > 1e-6;
        if (changed) zoomedRef.current = !(rng.from <= minF && rng.to >= maxT);
      }
      prevRange.current = rng;
    });

    return () => {
      chart.remove();
      chartRef.current = null;
    };
  }, [symbol, theme.palette.mode]);

  // ─── 2b) On each tick, update both series’ data ─────────────
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !data || !flowAcc) return;

    priceSeriesRef.current.setData(data);
    flowSeriesRef.current.setData(flowAcc);

    if (!zoomedRef.current) {
      chart.timeScale().fitContent();
    }
  }, [data, flowAcc]);

  // ─── 3) Telegram alerts when Flow/Price crosses H-Line ──────
  useEffect(() => {
    const arr = hTarget === 'price' ? data : flowAcc;
    if (!arr?.length) return;
    const lastPoint = arr[arr.length - 1];
    const last      = lastPoint.value;
    const hP        = hLinePriceRef.current;
    if (hP == null) return;

    if (last > hP && !notifiedRef.current) {
      notifiedRef.current = true;
      const crossedAt = new Date(lastPoint.time * 1000).toLocaleString();
      const text = [
        `${symbol} 🚀 ${hTarget.toUpperCase()} crossed H-Line!`,
        `Time:    ${crossedAt}`,
        `H-Line:  ${hP.toFixed(2)}`,
        `Now:     ${last.toFixed(2)}`,
      ].join('\n');

      fetch(`https://api.telegram.org/bot${BOT_TOKEN}/sendMessage`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ chat_id: CHAT_ID, text }),
      })
      .then(r => r.json().then(payload => {
        if (!r.ok) console.error('Telegram API error:', payload);
        else console.log('Alert sent', payload);
      }))
      .catch(console.error);
    }
    else if (last <= hP && notifiedRef.current) {
      notifiedRef.current = false;
    }
  }, [data, flowAcc, hTarget, symbol]);

  // ─── toolbar handlers & render ──────────────────────────────
  const resetZoom = () => {
    zoomedRef.current = false;
    chartRef.current?.timeScale().fitContent();
  };
  const toggleHLine = () => {
    hToolRef.current = !hToolRef.current;
    setHToolActive(hToolRef.current);
    if (!hToolRef.current && hLineRef.current) {
      const series = hTarget === 'price'
        ? priceSeriesRef.current
        : flowSeriesRef.current;
      series.removePriceLine(hLineRef.current);
      hLineRef.current      = null;
      hLinePriceRef.current = null;
      notifiedRef.current   = false;
    }
  };

  // ─── wrapper styles ─────────────────────────────────────────
  // No bottom‐padding—chart will be absolutely positioned to fill below toolbar.
  const wrapperSx = fullscreen
    ? {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 999,
        bgcolor: '#575b69',
      }
    : {
        position: 'relative',
        width: '100%',
        height: '100%',
        margin: '2px',
        bgcolor: '#575b69',
      };

  return (
    <Box sx={{ ...wrapperSx, borderRadius: 5, overflow: 'hidden' }}>
      {/* ── Toolbar region (fixed height: 48px) ─────────────────── */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          zIndex: 10,
        }}
      >
        {/* 1) Symbol picker (controlled) */}
        <Autocomplete
          size="small"
          options={symbolsList}
          value={symbol}                 /* ← bind the current symbol */
          onChange={(_, v) => {
            onSymbolChange(v);
            setLoading(true);
          }}
          sx={{ width: 200 }}
          renderInput={(params) => <TextField {...params} placeholder="Symbol…" />}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* 2) Notify dropdown */}
          <FormControl size="small">
            <InputLabel>Notify</InputLabel>
            <Select
              value={hTarget}
              label="Notify"
              onChange={(e) => {
                setHTarget(e.target.value);
                setHToolActive(false);
                hToolRef.current = false;
              }}
            >
              <MenuItem value="flow">Flow</MenuItem>
              <MenuItem value="price">Price</MenuItem>
            </Select>
          </FormControl>

          {/* 3) H-Line toggle */}
          <IconButton onClick={toggleHLine} color={hToolActive ? 'secondary' : 'default'}>
            <NotificationImportantIcon />
          </IconButton>

          {/* 4) Fullscreen button (only when not already fullscreen) */}
          {symbol && !fullscreen && (
            <IconButton
              onClick={() =>
                window.open(`/realtime_fullscreen?symbol=${symbol}`, '_blank')
              }
            >
              <ZoomInIcon />
            </IconButton>
          )}

          {/* 5) Reset Zoom */}
          <IconButton onClick={resetZoom}>
            <LoopIcon />
          </IconButton>
        </Box>
      </Box>

      {/* ── Loading overlay (below toolbar) ─────────────────────── */}
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: '48px',      // start right below the toolbar
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 5,
          }}
        >
          <CircularProgress sx={{ color: 'white' }} />
          <Typography sx={{ color: 'white', mt: 1 }}>Loading…</Typography>
        </Box>
      )}

      {/* ── Chart container fills from top=48px down to bottom=0 ──── */}
      <div
        ref={containerRef}
        style={{
          position: 'absolute',
          top: '48px',                 /* below toolbar */
          left: 0,
          right: 0,
          bottom: 0,                   /* flush to bottom of wrapper */
          zIndex: 0,
        }}
      />
    </Box>
  );
}
